<template>
  <v-container fluid class="pa-4">
    <div v-if="loading" class="pa-8 text-center"><v-progress-circular indeterminate /></div>
    <div v-else-if="!result" class="pa-8 text-center"><v-alert type="error">Resultado no disponible</v-alert></div>
    <div v-else>
      <div class="d-flex align-center mb-3 flex-wrap">
        <h2 class="text-h5 font-weight-bold">Resultado del Autodiagnóstico</h2>
        <v-spacer />
        <v-btn outlined :loading="sharing" @click="createShare">
          <v-icon left>mdi-share-variant</v-icon> Compartir
        </v-btn>
        <v-btn outlined class="ml-2" :loading="downloadingPdf" @click="downloadResultPdf">
          <v-icon left>mdi-file-pdf-box</v-icon> PDF
        </v-btn>
        <v-btn color="primary" class="ml-2" to="/sac">Realizar nuevo intento</v-btn>
      </div>

      <v-row>
        <v-col cols="12" md="4">
          <v-card class="pa-4 text-center" outlined>
            <SacIndicatorChart
                :value="Math.round(result.globalScore || 0)"
                :level="result.globalLevel"
                :color="(result.levelInfo && result.levelInfo.color) || '#1976D2'"
                height="240px"
            />
            <div class="mt-3 text-body-2 grey--text">{{ result.levelInfo && result.levelInfo.description }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="8">
          <v-card class="pa-4" outlined>
            <h3 class="text-h6 mb-2">Distribución por rubro</h3>
            <SacRadarChart :data="(result.charts && result.charts.radar) || {}" height="280px" />
          </v-card>
        </v-col>
      </v-row>

      <v-card class="mt-3 pa-4" outlined>
        <h3 class="text-h6 mb-2">Puntaje por sección</h3>
        <SacBarsChart :data="(result.charts && result.charts.bars) || {}" height="500px" />
      </v-card>

      <v-card v-if="recommendations.length" class="mt-3 pa-4" outlined>
        <h3 class="text-h6 mb-2">Recomendación</h3>
        <p class="text-body-1">{{ result.levelInfo && result.levelInfo.recommendation }}</p>
      </v-card>

      <v-card v-if="worstFlags.length" class="mt-3 pa-4" outlined>
        <h3 class="text-h6 mb-2">Áreas de mejora prioritarias</h3>
        <v-list>
          <v-list-item v-for="q in worstFlags" :key="q.questionId">
            <v-list-item-avatar>
              <v-icon :color="q.pct < 30 ? 'red' : q.pct < 60 ? 'orange' : 'green'">mdi-flag</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">{{ q.code }} — {{ q.pct.toFixed(1) }}%</v-list-item-title>
              <v-list-item-subtitle>{{ q.text }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>

      <v-dialog v-model="shareDialog" max-width="500">
        <v-card>
          <v-card-title>Compartir resultado</v-card-title>
          <v-card-text>
            <p class="mb-2">Comparte este enlace con quien desees. Quien lo abra verá el resultado sin necesidad de iniciar sesión.</p>
            <v-text-field
                v-model="shareUrl"
                label="Enlace público"
                readonly
                outlined
                dense
                append-icon="mdi-content-copy"
                @click:append-icon="copyShare"
            />
            <div v-if="shareExpiresAt" class="text-caption grey--text">
              Expira: {{ new Date(shareExpiresAt).toLocaleString('es-MX') }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn v-if="shareUrl" text color="error" :loading="revoking" @click="revokeCurrentShare">
              Revocar enlace
            </v-btn>
            <v-btn text @click="shareDialog = false">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import { mapMutations } from 'vuex';
import { getResult, createShare, revokeShare, downloadPdf } from '../../api/sac';
import SacIndicatorChart from '../../components/sac/SacIndicatorChart.vue';
import SacRadarChart from '../../components/sac/SacRadarChart.vue';
import SacBarsChart from '../../components/sac/SacBarsChart.vue';

export default {
    name: 'SacMemberResult',
    components: { SacIndicatorChart, SacRadarChart, SacBarsChart },
    data() {
        return {
            loading: true,
            result: null,
            sharing: false,
            downloadingPdf: false,
            revoking: false,
            shareDialog: false,
            shareUrl: '',
            shareExpiresAt: null,
        };
    },
    computed: {
        worstFlags() {
            const scored = (this.result?.questionScores || []).filter((q) => !q.skipped && Number(q.max) > 0);
            return scored
                .map((q) => ({ ...q, pct: q.max > 0 ? (q.score / q.max) * 100 : 0 }))
                .sort((a, b) => a.pct - b.pct)
                .slice(0, 3);
        },
        recommendations() {
            return this.result?.levelInfo?.recommendation ? [this.result.levelInfo.recommendation] : [];
        },
    },
    async mounted() {
        try {
            const id = this.$route.params.id;
            this.result = await getResult(id);
        } catch (e) {
            this.show({
                text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo cargar el resultado',
                color: 'error',
            });
        } finally {
            this.loading = false;
        }
    },
    methods: {
        ...mapMutations('notification', ['show']),
        async createShare() {
            this.sharing = true;
            try {
                const r = await createShare(this.$route.params.id, null);
                this.shareUrl = `${window.location.origin}${r.shareUrl}`;
                this.shareExpiresAt = r.expiresAt;
                this.shareDialog = true;
                this.show({ text: 'Enlace para compartir creado correctamente', color: 'success' });
            } catch (e) {
                this.show({
                    text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo crear el enlace para compartir',
                    color: 'error',
                });
            } finally {
                this.sharing = false;
            }
        },
        async copyShare() {
            try {
                await navigator.clipboard.writeText(this.shareUrl);
                this.show({ text: 'Enlace copiado al portapapeles', color: 'success' });
            } catch (e) {
                this.show({ text: 'No se pudo copiar el enlace', color: 'error' });
            }
        },
        async revokeCurrentShare() {
            this.revoking = true;
            try {
                await revokeShare(this.$route.params.id);
                this.shareUrl = '';
                this.shareExpiresAt = null;
                this.shareDialog = false;
                this.show({ text: 'Enlace revocado correctamente', color: 'success' });
            } catch (e) {
                this.show({
                    text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo revocar el enlace',
                    color: 'error',
                });
            } finally {
                this.revoking = false;
            }
        },
        async downloadResultPdf() {
            this.downloadingPdf = true;
            try {
                const blob = await downloadPdf(this.$route.params.id);
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `sac-resultado-${this.$route.params.id}.pdf`;
                link.click();
                URL.revokeObjectURL(url);
            } catch (e) {
                this.show({
                    text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo descargar el PDF',
                    color: 'error',
                });
            } finally {
                this.downloadingPdf = false;
            }
        },
    },
};
</script>
